import { PublicMenuCategory } from '../../core/models/menu'
import { useTranslation } from '../../core/hooks/useTranslation'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import Badge from '../../components/ui/Badge'

interface CategoryTabsProps {
  categories: PublicMenuCategory[]
  selectedCategory: PublicMenuCategory | null
  onSelectCategory: (category: PublicMenuCategory | null) => void
}

export default function CategoryTabs({ categories, selectedCategory, onSelectCategory }: CategoryTabsProps) {
  const { t } = useTranslation()
  const { color, darker, opacity15 } = useBusinessColor()

  if (categories.length === 0) {
    return null
  }

  return (
    <div className="mb-4 sm:mb-6">
      <nav className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide pb-1" aria-label={t('categories')}>
        {categories.map((category) => {
          const isSelected = selectedCategory?.id === category.id
          
          return (
            <button
              key={category.id}
              onClick={() => onSelectCategory(category)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isSelected
                  ? 'text-white shadow-sm'
                  : 'bg-white/80 text-stone-700 border border-stone-200'
              }`}
              style={isSelected ? {
                background: `linear-gradient(to right, ${color}, ${darker})`,
              } : {}}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = opacity15
                  e.currentTarget.style.color = color
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                  e.currentTarget.style.color = '#292524'
                }
              }}
              aria-label={`${t('categories')}: ${category.name}`}
              aria-pressed={isSelected}
            >
              <span>{category.name}</span>
              {category.availableItemCount > 0 && (
                <Badge 
                  variant={isSelected ? 'primary' : 'gray'} 
                  size="sm"
                  className={`text-xs ${isSelected ? 'bg-white/20 text-white' : ''}`}
                  aria-label={`${category.availableItemCount} ${t('itemCount')}`}
                >
                  {category.availableItemCount}
                </Badge>
              )}
            </button>
          )
        })}
      </nav>
    </div>
  )
}